import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const STATS = [
  { value: "2018", label: "설립 연도", icon: "ri-building-line" },
  { value: "15,000+", label: "설치 완료", icon: "ri-home-4-line" },
  { value: "99.8%", label: "보안 신뢰도", icon: "ri-shield-check-line" },
  { value: "4.9점", label: "고객 만족도", icon: "ri-star-fill" },
];

const VALUES = [
  {
    icon: "ri-shield-keyhole-line",
    title: "최고의 보안",
    desc: "군사급 AES-256 암호화와 다중 인증 시스템으로 어떤 위협에도 흔들리지 않는 완벽한 보안을 제공합니다.",
  },
  {
    icon: "ri-award-line",
    title: "프리미엄 품질",
    desc: "1년 품질 보증. 수만 회 반복 테스트를 통과한 제품만 출시하며, 내구성과 신뢰성을 최우선으로 합니다.",
  },
  {
    icon: "ri-tools-line",
    title: "전문 설치 서비스",
    desc: "자격증 보유 전문 기사가 직접 방문해 당일 설치 완료. 설치 후 사용법 안내까지 책임집니다.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "24시간 고객 지원",
    desc: "설치 후에도 언제든지 연락 가능. 빠른 AS와 전문 상담으로 고객 곁에 항상 함께합니다.",
  },
];

const HISTORY = [
  { year: "2018", title: "도어준 설립", desc: "경기도 의정부에서 프리미엄 도어락 전문 기업으로 출발" },
  { year: "2020", title: "스마트 도어락 라인업 출시", desc: "지문인식 + 앱 연동 복합 인증 시스템 첫 출시" },
  { year: "2022", title: "누적 설치 5,000건 달성", desc: "서울·경기·인천 전 지역 당일 설치 서비스 확대" },
  { year: "2026", title: "누적 설치 15,000건 돌파", desc: "고객 만족도 4.9점, 업계 최고 수준 달성" },
];

const AboutPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />
    <main>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={{ minHeight: "92vh" }}>
        <img
          src="https://public.readdy.ai/ai/img_res/dd433e98e005d6bd468dfd32f51a198c.jpg"
          alt="도어준 쇼룸"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 h-full flex flex-col justify-center pt-32 pb-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-white/40" />
              <p className="text-[10px] font-bold tracking-widest uppercase text-white/50">Our Story · Since 2018</p>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-none mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              보안의<br />
              <em className="not-italic text-white/40">새 기준</em>
            </h1>
            <p className="text-base text-white/60 leading-relaxed max-w-md mb-10">
              도어준은 "집의 첫 번째 보안은 현관문에서 시작된다"는 철학으로,
              최첨단 기술과 프리미엄 디자인을 결합해 대한민국 도어락 시장을 선도합니다.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {STATS.map((s) => (
                <div key={s.label} className="bg-white/8 border border-white/10 rounded-2xl p-4" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                  <div className="w-7 h-7 flex items-center justify-center mb-2">
                    <i className={`${s.icon} text-white/40 text-base`} />
                  </div>
                  <p className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-[10px] text-white/40 mt-0.5 tracking-wide">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Brand Philosophy ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-3">Philosophy</p>
              <h2 className="text-3xl md:text-4xl font-black text-stone-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                기술이 아닌,<br />신뢰를 설치합니다
              </h2>
              <p className="text-sm text-stone-500 leading-relaxed mb-4">
                도어준은 단순한 도어락 판매·설치 업체가 아닙니다. 고객의 가정과 사업장에 안심을 설치하는 보안 파트너입니다.
              </p>
              <p className="text-sm text-stone-500 leading-relaxed mb-8">
                모든 설치 현장에서 자격증 보유 전문 기사가 직접 시공하며, 설치 후에도 지속적인 AS와 상담으로 고객 곁에 함께합니다.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="tel:01097939490"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-bold px-6 py-3 rounded-full hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  010-9793-9490
                </a>
                <a
                  href="https://blog.naver.com/qordudwns12"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 border border-stone-200 text-stone-600 text-sm font-semibold px-6 py-3 rounded-full hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-article-line" />
                  블로그 보기
                </a>
              </div>
            </div>

            {/* Right: Image stack */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: "4/5" }}>
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20door%20lock%20technician%20in%20clean%20uniform%20carefully%20installing%20premium%20smart%20digital%20lock%20on%20luxury%20apartment%20door%2C%20focused%20expert%20hands%20with%20precision%20tools%2C%20warm%20interior%20lighting%2C%20high%20end%20residential%20hallway%2C%20cinematic%20depth%20of%20field%2C%20ultra%20realistic%20professional%20photography&width=700&height=875&seq=about_tech_pro1&orientation=portrait"
                  alt="도어준 전문 설치"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-5 border border-stone-100 w-52" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}>
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-xs" />
                    </div>
                  ))}
                </div>
                <p className="text-sm font-black text-stone-900 mb-0.5">98% 고객 만족도</p>
                <p className="text-[10px] text-stone-400">2,400+ 리뷰 기준</p>
              </div>
              {/* Top badge */}
              <div className="absolute -top-4 -right-4 bg-stone-900 text-white rounded-2xl p-4 text-center w-28">
                <p className="text-2xl font-black" style={{ fontFamily: "'Playfair Display', serif" }}>7년+</p>
                <p className="text-[10px] text-stone-400 mt-0.5">업계 경력</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2">Core Values</p>
            <h2 className="text-3xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>도어준의 약속</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <div key={v.title} className="bg-white rounded-2xl p-6 border border-stone-100">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 flex items-center justify-center bg-stone-100 rounded-xl">
                    <i className={`${v.icon} text-stone-700 text-lg`} />
                  </div>
                  <span className="text-4xl font-black text-stone-100" style={{ fontFamily: "'Playfair Display', serif" }}>
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-sm font-black text-stone-900 mb-2">{v.title}</h3>
                <p className="text-xs text-stone-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── History ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left */}
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-3">History</p>
              <h2 className="text-3xl md:text-4xl font-black text-stone-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                도어준의<br />걸어온 길
              </h2>
              <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: "4/3" }}>
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20smart%20door%20lock%20product%20lineup%20display%2C%20multiple%20models%20arranged%20elegantly%20on%20clean%20white%20surface%2C%20professional%20studio%20photography%2C%20dramatic%20side%20lighting%2C%20minimalist%20product%20showcase%2C%20high%20end%20security%20devices%2C%20dark%20background%20with%20accent%20lighting&width=700&height=525&seq=about_lineup_pro1&orientation=landscape"
                  alt="도어준 제품 라인업"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right: Timeline */}
            <div className="space-y-0">
              {HISTORY.map((h, i) => (
                <div key={h.year} className="flex gap-5">
                  {/* Line */}
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-stone-900 rounded-full flex-shrink-0">
                      <span className="text-[10px] font-black text-white">{h.year.slice(2)}</span>
                    </div>
                    {i < HISTORY.length - 1 && <div className="w-px flex-1 bg-stone-200 my-2" />}
                  </div>
                  {/* Content */}
                  <div className={`pb-8 ${i === HISTORY.length - 1 ? "" : ""}`}>
                    <p className="text-xs font-bold text-stone-400 mb-1">{h.year}</p>
                    <h3 className="text-sm font-black text-stone-900 mb-1">{h.title}</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden py-24">
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20apartment%20building%20entrance%20at%20night%2C%20warm%20interior%20lighting%20visible%20through%20glass%20door%2C%20premium%20residential%20complex%2C%20elegant%20architecture%2C%20dark%20moody%20atmosphere%2C%20professional%20architectural%20photography%2C%20cinematic%20lighting&width=1600&height=600&seq=about_cta_pro1&orientation=landscape"
          alt="도어준 설치 문의"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <p className="text-xs font-bold tracking-widest uppercase text-white/40 mb-3">Get Started</p>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            지금 바로 상담하세요
          </h2>
          <p className="text-sm text-white/50 mb-8">서울 · 경기 · 인천 당일 설치 가능 · 전문 기사 직접 시공</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a
              href="tel:01097939490"
              className="inline-flex items-center gap-2 bg-white text-stone-900 text-sm font-black px-8 py-3.5 rounded-full hover:bg-stone-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill" />
              010-9793-9490
            </a>
            <a
              href="https://open.kakao.com/o/srLtta1b"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 text-sm font-bold px-8 py-3.5 rounded-full whitespace-nowrap cursor-pointer transition-colors"
              style={{ backgroundColor: "#FEE500", color: "#3A1D1D" }}
            >
              <i className="ri-chat-smile-2-line" />
              카카오 상담
            </a>
          </div>
        </div>
      </section>

    </main>
    <Footer />
  </div>
);

export default AboutPage;
